import React, { useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Typography } from "@mui/material";

import messages from "../../../assets/locale/messages";
import { UIActionModal, UITextarea } from "../../ui";
import {
  fillTemplate,
  formatAmount,
} from "../../Events/Form/Steps/ChooseCrewSize/crewSizeUtils";
import CompanyStatusWarning from "../CompanyStatusWarning";

import "./DeactivateCompanyModal.scss";

/** The backend has no limit: the same 300 as the evaluation feedback and the report comments. */
const REASON_MAX_LENGTH = 300;

/**
 * "Deactivate Company Account": the Super Admin confirms stopping a company, with a mandatory
 * reason. Mounted on open, so the reason starts empty each time.
 */
const DeactivateCompanyModal = ({ company, saving, onClose, onConfirm }) => {
  const lang = useSelector((state) => state.locale.lang);
  const t = messages[lang].companiesList.deactivation;
  const [reason, setReason] = useState("");
  const trimmedReason = reason.trim();
  const eventsCount = company.number_of_running_events;

  const companyName =
    (lang === "ar" ? company.name_ar : company.name_en) ||
    company.name_en ||
    company.name_ar;

  return (
    <UIActionModal
      open
      variant="reject"
      maxWidth="xs"
      className="deactivate-company-modal"
      title={t.title}
      onClose={() => !saving && onClose()}
      actions={
        <UIActionModal.Actions
          cancelLabel={t.cancel}
          confirmLabel={t.confirm}
          confirmVariant="reject"
          confirmDisabled={!trimmedReason || reason.length > REASON_MAX_LENGTH}
          confirmLoading={saving}
          onCancel={() => !saving && onClose()}
          onConfirm={() => onConfirm({ reason: trimmedReason })}
        />
      }
    >
      <UIActionModal.Divider />
      <Typography component="p" className="ui-action-modal__body-text deactivate-company-modal__message">
        {fillTemplate(t.message, { company: companyName })}
      </Typography>
      <CompanyStatusWarning
        variant="error"
        title={
          eventsCount === 1
            ? t.eventsCountOne
            : fillTemplate(t.eventsCount, { count: formatAmount(eventsCount) })
        }
        items={t.warnings}
      />
      <UITextarea
        label={t.reasonLabel}
        required
        name="deactivationReason"
        value={reason}
        placeholder={t.reasonPlaceholder}
        onValueChange={setReason}
        minRows={3}
        maxLength={REASON_MAX_LENGTH}
        maxLengthMessage={t.reasonMaxLengthError}
        classNames={{ root: "deactivate-company-modal__reason" }}
      />
    </UIActionModal>
  );
};

DeactivateCompanyModal.propTypes = {
  /** The companies-list row being deactivated. */
  company: PropTypes.shape({
    name_en: PropTypes.string,
    name_ar: PropTypes.string,
    /** Upcoming and Ongoing events the deactivation cancels. */
    number_of_running_events: PropTypes.number,
  }).isRequired,
  /** The deactivation is being sent: the modal can't be confirmed or closed meanwhile. */
  saving: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  /** Called with `{ reason }`, the reason trimmed. */
  onConfirm: PropTypes.func.isRequired,
};

DeactivateCompanyModal.defaultProps = {
  saving: false,
};

export default DeactivateCompanyModal;
